import { Outlet } from 'react-router';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import {
  DocumentScanner,
  Message,
  RouteOutlined,
  LocalGasStation,
  Logout as LogoutIcon,
  GpsFixed,
  InfoOutlined,
  MoreHoriz,
  TrackChanges,
  AlternateEmailOutlined,
  EditNote,
  Settings,
  ShareLocationOutlined,
  Attribution,
  InsertChartOutlined,
  DownloadForOfflineOutlined,
  ManageAccounts,
  Tune,
  ImportantDevices,
  Timeline,
  Analytics,
} from '@mui/icons-material';
import { Badge } from '@mui/material';

import { type ExtraFeature } from '@/app/routing/guards/extraFeatures';
import { type PopupType } from '@/types';
import { ContactsPopup, MessagesPopup, More, TrackingPopup, RoutesPopup, StreamChatPopup } from '@/components';
import { RefuelsPopup } from '@/features/refuels';
import { TimeSeriesChartPopup } from '@/features/time-series-chart';
import { AppErrorBoundary } from '@/shared/ui/app-error-boundary/AppErrorBoundary';
import ReportPopup from '@/components/report/ReportPopup';
import StreamChatMenuIcon from '@/components/stream-chat/StreamChatMenuIcon';
import TachographPopup from '@/components/tachograph/fileDownload/TachographPopup';
import FeedbackForm from '@/components/contacts/FeedbackForms';
import { TachographDiagramPopup } from '@/components/tachograph/diagram/TachographDiagramPopup';
import SysinfoPopup from '@/components/sysinfo/SysinfoPopup';
import EtransportIcon from '@/components/etransport/utils/EtransportSvgIcons';
import EtransportPopup from '@/components/etransport/Etransport';
import { Map } from '@/components';
import { Logout } from './Logout';
import { MinimalUser } from '@/generated/model';
import SharePopup from '@/components/tracking/SharePopup';
import SysAdminPopup from '@/components/sysadmin/SysAdminPopup';
import ProfilePopup from '@/components/settings/ProfilePopup';
import TargetAttributesPopup from '@/components/targetAttributes/TargetAttributesPopup';
import ShareableLinkPopup from '@/components/shareableLinks/ShareableLinksPopup';
import { UserAccessControlPopup, userAccessRoutes } from '@/features/user-access-control';

const newMessageCount = 0;

function reportPopup(closePopup: () => Promise<void>, isBottom: boolean): PopupType {
  return {
    title: 'mainMenu.reports',
    releaseDate: '2026-09-01',
    icon: <DocumentScanner fontSize={isBottom ? 'small' : 'medium'} />,
    element: <ReportPopup closePopup={closePopup} isBottom={isBottom} />,
    showToken: false,
    mainFunction: true,
    showMobile: true,
    requiredExtraFeature: 'extrafeature_pluto_report_tab' as const satisfies ExtraFeature,
    dataQaElementId: 'more-list-item-reports',
    level: 1,
    path: '/reports',
  };
}

function timeSeriesChartPopup(isBottom: boolean): PopupType {
  return {
    title: 'mainMenu.timeSeriesChart',
    releaseDate: '2026-10-03',
    icon: <Timeline fontSize={isBottom ? 'small' : 'medium'} />,
    element: (
      <AppErrorBoundary boundaryId="time-series-chart-workflow">
        <TimeSeriesChartPopup data-qa-element-id="time-series-chart-popup" />
      </AppErrorBoundary>
    ),
    showToken: false,
    mainFunction: true,
    showMobile: true,
    requiredExtraFeature: 'extrafeature_pluto_time_series_chart' as const satisfies ExtraFeature,
    dataQaElementId: 'more-list-item-time-series-chart',
    level: isBottom ? 1 : 2,
    path: 'time-series-chart',
  };
}

function analysisSubmenu(closePopup: () => Promise<void>, isBottom: boolean): PopupType[] {
  return [reportPopup(closePopup, isBottom), timeSeriesChartPopup(isBottom)] satisfies PopupType[];
}

function analyticsPopup(closePopup: () => Promise<void>, isBottom: boolean): PopupType {
  // until we don't rearrage the phone menu layout
  if (isBottom) {
    return timeSeriesChartPopup(isBottom);
  }

  return {
    title: 'mainMenu.analytics',
    releaseDate: '2026-10-03',
    icon: <Analytics fontSize="medium" />,
    showToken: false,
    mainFunction: true,
    showMobile: false,
    dataQaElementId: 'left-navigation-item-analytics',
    children: analysisSubmenu(closePopup, isBottom),
    level: -1,
    path: '/analytics',
  };
}

export function getAdditionalElements(
  closePopup: () => Promise<void>,
  isBottom: boolean,
  hasTachograph?: boolean,
  showUserAccessControl = isBottom,
  includeAnalytics = true
): PopupType[] {
  const userAccessControlElement = (
    <UserAccessControlPopup closePopup={closePopup} data-qa-element-id="user-access-control-popup" />
  );

  const analysisElements = includeAnalytics
    ? [
        reportPopup(closePopup, isBottom),
        // TODO: Add the time-series chart to the mobile menu once the mobile layout is implemented.
        ...(!isBottom ? [analyticsPopup(closePopup, isBottom)] : []),
      ]
    : [];

  const additionalElements: PopupType[] = [
    ...analysisElements,
    {
      title: 'mainMenu.shareableLinks',
      releaseDate: '2026-05-01',
      icon: <ShareLocationOutlined fontSize={isBottom ? 'small' : 'medium'} />,
      element: <ShareableLinkPopup closePopup={closePopup} isBottom={isBottom} />,
      showToken: false,
      mainFunction: true,
      showMobile: true,
      dataQaElementId: 'more-list-item-shareable-links',
      level: 2,
      path: 'shareablelinks',
    },
    {
      title: 'mainMenu.etransport',
      releaseDate: '2026-02-20',
      icon: <EtransportIcon fontSize={isBottom ? 'small' : 'medium'} />,
      element: <EtransportPopup close={close} data-qa-element-id="etransport-popup" />,
      showToken: false,
      mainFunction: true,
      showMobile: true,
      level: isBottom ? 1 : 2,
      path: 'etransport',
      requiredExtraFeature: 'extrafeature_pluto_etransport' as const satisfies ExtraFeature,
      dataQaElementId: 'more-list-item-etransport',
    },
    ...(showUserAccessControl
      ? [
          {
            title: 'mainMenu.userAccessControl',
            icon: <Attribution fontSize="small" />,
            element: userAccessControlElement,
            showToken: false,
            mainFunction: true,
            showMobile: true,
            level: 1,
            path: userAccessRoutes.root,
            activeOnSubRoutes: true,
            requiredExtraFeature: 'user_access_control' as const satisfies ExtraFeature,
            dataQaElementId: 'more-list-item-user-access-control',
          },
          {
            element: userAccessControlElement,
            showToken: false,
            mainFunction: true,
            showMobile: false,
            level: 1,
            path: userAccessRoutes.userDetailsPattern,
            hidden: true,
            requiredExtraFeature: 'extrafeature_pluto_user_access_control' as const satisfies ExtraFeature,
          },
          {
            element: userAccessControlElement,
            showToken: false,
            mainFunction: true,
            showMobile: false,
            level: 1,
            path: userAccessRoutes.userFormPattern,
            hidden: true,
            requiredExtraFeature: 'extrafeature_pluto_user_access_control' as const satisfies ExtraFeature,
          },
        ]
      : []),
    {
      title: 'mainMenu.settings',
      releaseDate: '2026-04-30',
      icon: <Settings fontSize={isBottom ? 'small' : 'medium'} />,
      showToken: false,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'more-list-item-settings',
      children: profileSubmenu(closePopup, isBottom),
      level: -1,
      path: '/settings',
    },
    {
      title: 'mainMenu.contacts',
      icon: <InfoOutlined fontSize={isBottom ? 'small' : 'medium'} />,
      showToken: true,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'more-list-item-contacts',
      children: contactSubmenu(closePopup, isBottom),
      level: -1,
      path: '/contacts',
    },
    {
      title: 'mainMenu.logout',
      icon: <LogoutIcon fontSize={isBottom ? 'small' : 'medium'} />,
      element: <Logout />,
      showToken: false,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'more-list-item-logout',
      level: 1,
      path: '/logout',
    },
    {
      element: <TargetAttributesPopup closePopup={closePopup} />,
      showToken: false,
      mainFunction: false,
      showMobile: true,
      show: () => false,
      hidden: true,
      level: 1,
      path: '/targetattreditor',
    },
  ] satisfies PopupType[];

  if (hasTachograph) {
    additionalElements.unshift({
      title: 'mainMenu.tachograph',
      releaseDate: '2026-01-10',
      icon: <TrackChanges fontSize={isBottom ? 'small' : 'medium'} />,
      showToken: false,
      mainFunction: true,
      showMobile: true,
      dataQaElementId: 'more-list-item-tachograph',
      children: tachographSubmenu(closePopup, isBottom),
      level: 1,
      path: '/tachograph',
    });
  }

  return additionalElements;
}

function tachographSubmenu(closePopup: () => Promise<void>, isBottom: boolean): PopupType[] {
  const subMenu = [
    {
      title: 'mainMenu.tachographSubmenu.fileDownload',
      releaseDate: '2026-01-10',
      icon: <DownloadForOfflineOutlined fontSize={'small'} />,
      element: <TachographPopup closePopup={closePopup} isBottom={isBottom} />,
      showToken: false,
      mainFunction: true,
      showMobile: true,
      dataQaElementId: 'tachograph_file_download_menu_item',
      level: 1,
      path: '/tachograph/download',
    },
    {
      title: 'mainMenu.tachographSubmenu.chart',
      releaseDate: '2026-01-10',
      icon: <InsertChartOutlined fontSize={'small'} />,
      element: <TachographDiagramPopup closePopup={closePopup} isBottom={isBottom} />,
      showToken: false,
      mainFunction: true,
      showMobile: false,
      dataQaElementId: 'tachograph_chart_menu_item',
      level: 1,
      path: '/tachograph/chart',
    },
  ] satisfies PopupType[];

  return subMenu;
}

function contactSubmenu(closePopup: () => Promise<void>, isBottom: boolean): PopupType[] {
  const subMenu = [
    {
      title: 'mainMenu.contactSubmenu.contactInformationTitle',
      icon: <AlternateEmailOutlined fontSize={isBottom ? 'small' : 'medium'} />,
      element: <ContactsPopup closePopup={closePopup} />,
      showToken: true,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'contact_information_menu_item',
      level: 2,
      path: 'contact',
    },
    {
      title: 'mainMenu.contactSubmenu.feedback',
      releaseDate: '2026-01-20',
      icon: <EditNote fontSize={isBottom ? 'small' : 'medium'} />,
      element: <FeedbackForm closePopup={closePopup} />,
      showToken: true,
      mainFunction: true,
      showMobile: false,
      dataQaElementId: 'feedback_menu_item',
      level: 2,
      path: 'feedback',
    },
    {
      title: 'mainMenu.systemInformation',
      icon: <ImportantDevices fontSize={isBottom ? 'small' : 'medium'} />,
      element: <SysinfoPopup closePopup={closePopup} />,
      showToken: false,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'more-list-item-systemInfo',
      level: 2,
      path: 'sysinfo',
    },
  ] satisfies PopupType[];

  return subMenu;
}

function profileSubmenu(closePopup: () => Promise<void>, isBottom: boolean): PopupType[] {
  const subMenu = [
    {
      title: 'mainMenu.profile',
      releaseDate: '2026-04-30',
      icon: <ManageAccounts fontSize={'small'} />,
      element: <ProfilePopup closePopup={closePopup} data-qa-element-id="settings-popup" />,
      showToken: false,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'more-list-item-systemSettings',
      level: 2,
      path: 'profile',
    },
    {
      title: 'systemInfo.system',
      icon: <Tune fontSize={isBottom ? 'small' : 'medium'} />,
      element: <SysAdminPopup closePopup={closePopup} />,
      showToken: false,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'more-list-item-systemInfo',
      level: 2,
      path: 'sysadmin',
    },
  ] satisfies PopupType[];

  return subMenu;
}

export function popupDefs({
  closePopup,
  isBottom,
  user,
}: {
  closePopup: () => Promise<void>;
  isBottom: boolean;
  user: MinimalUser;
}): PopupType[] {
  // FIXME ez igy nem lazy loading ?
  const arr: PopupType[] = [
    {
      title: 'mainMenu.tracking',
      icon: <GpsFixed fontSize={isBottom ? 'small' : 'medium'} />,
      element: (
        <>
          <Outlet />
          <AppErrorBoundary boundaryId="tracking-map">
            <Map />
          </AppErrorBoundary>
        </>
      ), // ? az jo h ebben van az Outlet ?
      showToken: isBottom, // desktopon nem mutatjuk a token usernek
      mainFunction: true,
      showMobile: true,
      level: 1,
      path: '/tracking',
      navigateTo: '/tracking/select',
    },
    {
      element: (
        <AppErrorBoundary boundaryId="tracking-workflow">
          <TrackingPopup data-qa-element-id="tracking-popup" />
          <Outlet />
        </AppErrorBoundary>
      ),
      showToken: isBottom, // desktopon nem mutatjuk a token usernek
      mainFunction: true,
      showMobile: true,
      level: 2,
      path: '/tracking/select',
      hidden: true,
    },
    {
      element: <SharePopup />,
      showToken: false,
      mainFunction: true,
      showMobile: true,
      level: 3,
      path: '/tracking/select/share',
      hidden: true,
    },
    {
      title: 'mainMenu.routes',
      icon: <RouteOutlined fontSize={isBottom ? 'small' : 'medium'} sx={{ transform: 'rotate(90deg)' }} />,
      element: (
        <>
          <Outlet />
          <AppErrorBoundary boundaryId="routes-map">
            <Map />
          </AppErrorBoundary>
        </>
      ), // ? az jo h ebben van az Outlet ?,
      showToken: false,
      mainFunction: true,
      showMobile: true,
      level: 1,
      path: '/routes',
      navigateTo: '/routes/select',
    },
    {
      element: (
        <AppErrorBoundary boundaryId="routes-workflow">
          <RoutesPopup data-qa-element-id="routes-popup" />
        </AppErrorBoundary>
      ),
      showToken: false,
      mainFunction: true,
      showMobile: true,
      level: 2,
      path: '/routes/select',
      hidden: true,
    },
    ...(!isBottom ? [analyticsPopup(closePopup, isBottom)] : []),
    {
      title: 'mainMenu.messages',
      icon:
        newMessageCount > 0 ? (
          <Badge color="secondary" variant="dot">
            <Message fontSize={isBottom ? 'small' : 'medium'} />
          </Badge>
        ) : (
          <Message fontSize={isBottom ? 'small' : 'medium'} />
        ),
      element: (
        <QueryErrorResetBoundary>
          {({ reset }) => (
            <AppErrorBoundary boundaryId="messages-workflow" onReset={reset}>
              <MessagesPopup closePopup={closePopup} data-qa-element-id="messages-popup" />
            </AppErrorBoundary>
          )}
        </QueryErrorResetBoundary>
      ),
      showToken: false,
      mainFunction: true,
      showMobile: true,
      show: (user?: MinimalUser, chatId?: string) =>
        !(user?.extraFeatures?.includes('extrafeature_pluto_getstreamio_chat') ?? false) || !chatId,
      level: isBottom ? 1 : 2, // mobilon nem kell ala terkep
      path: 'messages',
    },
    {
      title: 'mainMenu.streamMessages',
      icon: <StreamChatMenuIcon isBottom={isBottom} />,
      element: (
        <AppErrorBoundary boundaryId="stream-chat-workflow">
          <StreamChatPopup closePopup={closePopup} data-qa-element-id="stream-messages-popup" />
        </AppErrorBoundary>
      ),
      showToken: false,
      mainFunction: true,
      showMobile: true,
      show: (user?: MinimalUser, chatId?: string) =>
        !!user && (user.extraFeatures?.includes('extrafeature_pluto_getstreamio_chat') ?? false) && !!chatId,
      requiredExtraFeature: 'extrafeature_pluto_getstreamio_chat' as const satisfies ExtraFeature,
      level: isBottom ? 1 : 2, // mobilon nem kell ala terkep
      path: 'chat',
    },
    {
      title: 'mainMenu.refuels',
      icon: <LocalGasStation fontSize={isBottom ? 'small' : 'medium'} />,
      element: (
        <AppErrorBoundary boundaryId="refuels-popup">
          <RefuelsPopup closePopup={closePopup} data-qa-element-id="refuels-popup" />
        </AppErrorBoundary>
      ),
      showToken: false,
      mainFunction: true,
      showMobile: true,
      level: isBottom ? 1 : 2, // mobilon nem kell ala terkep
      path: 'refuels',
    },
  ] satisfies PopupType[];

  let additionalElement: PopupType[];
  if (isBottom) {
    additionalElement = [
      {
        title: 'mainMenu.more',
        releaseDate: '2026-09-01',
        icon: <MoreHoriz fontSize="small" />,
        element: (
          <More closePopup={closePopup} hasTachograph={user.hasTachograph} data-qa-element-id="more-menu-list" />
        ),
        showToken: true,
        mainFunction: false,
        showMobile: true,
        level: 2,
        path: 'more',
        navigateTo: 'more',
      },
    ] satisfies PopupType[];
  } else {
    additionalElement = getAdditionalElements(closePopup, isBottom, user.hasTachograph, false, false);
  }

  return arr.concat(additionalElement);
}
